import { useContext, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import StatCard from '../../components/StatCard';
import { listInventoryLevels } from '../../utils/queries';

const PAGE_SIZE = 25;
const LOW_STOCK_THRESHOLD = 10;

export default function InventoryLevelsList() {
	const { activeStoreId } = useContext(MyContext);
	const [levels, setLevels] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [lowStockCount, setLowStockCount] = useState(0);
	const [outOfStockCount, setOutOfStockCount] = useState(0);

	useEffect(() => {
		setLoading(true);
		listInventoryLevels({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
			storeId: activeStoreId || undefined,
		}).then((result) => {
			if (result?.success) {
				setLevels(result.data.levels);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	}, [page, search, activeStoreId]);

	useEffect(() => {
		listInventoryLevels({ limit: 200, storeId: activeStoreId || undefined }).then((result) => {
			if (result?.success) {
				const allLevels = result.data.levels;
				setLowStockCount(
					allLevels.filter((l) => l.quantityAvailable > 0 && l.quantityAvailable <= LOW_STOCK_THRESHOLD).length,
				);
				setOutOfStockCount(allLevels.filter((l) => l.quantityAvailable <= 0).length);
			}
		});
	}, [activeStoreId]);

	const columns = [
		{ key: 'product_name', header: 'Product' },
		{ key: 'erpProductNumber', header: 'ERP #' },
		{ key: 'location', header: 'Location' },
		{ key: 'quantityOnHand', header: 'On Hand' },
		{ key: 'quantityAvailable', header: 'Available' },
		{
			key: 'lastUpdatedAt',
			header: 'Last Updated',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>Inventory Levels</h1>

			<div className='grid grid-cols-3 gap-4 mb-8'>
				<StatCard label='Total SKUs' value={total} description='Active product variants' />
				<StatCard
					label='Low Stock'
					value={lowStockCount}
					description={`${LOW_STOCK_THRESHOLD} units or less`}
					valueClassName='text-amber-500'
				/>
				<StatCard
					label='Out of Stock'
					value={outOfStockCount}
					description='Unavailable for sale'
					valueClassName='text-red-500'
				/>
			</div>

			<div className='mb-5'>
				<label>Search (ERP #)</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={levels} loading={loading} emptyHeading='No inventory levels found' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
